import { Injectable, inject } from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map, shareReplay} from 'rxjs/operators';
import {AuthenticationService} from '@app/services/authentication.service';

/** The password rules the server publishes on GET /api/v1/auth/policy/public. */
export interface PasswordPolicy {
  minLength: number;
  maxLength: number;
  requireLowercase: boolean;
  requireUppercase: boolean;
  requireNumber: boolean;
  requireSpecialCharacter: boolean;
  specialCharacters: string;
  contextDenylist: string[];
  contextMinimumCharacters: number;
  sequenceLength: number;
  commonPasswords: string[];
}

/**
 * A small list kept in the bundle purely so the live hints have something to say before,
 * or instead of, the server's answer. The real list is a server-side table checked at
 * every write and at login; this is a convenience, never the control.
 */
export const DEFAULT_COMMON_PASSWORDS: string[] = [
  'password', 'password123', '123456789', 'qwerty123', 'letmein',
  'welcome123', 'admin123', 'iloveyou', 'snoball', 'snowball',
  '123456789012345', '1234567890123456', 'passwordpassword',
  'qwertyuiopasdfgh', 'qwertyuiop12345', 'administrator',
  'administrator123', 'changemechangeme', 'letmeinletmein',
  'welcome123456789', 'footballfootball', 'monkeymonkey123',
  'sunshinesunshine', 'princessprincess', 'trustno1trustno1',
  'abc123abc123abc', 'dragonballz12345', 'correcthorsebatterystaple'
];

/**
 * What the rules are when the server has not answered.
 *
 * These MUST match the shipped server defaults in `Config.authPolicy.password`. They are
 * what every password field falls back to when the policy endpoint is unreachable, so a
 * network failure degrades the hints rather than locking anyone out of a form.
 */
export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  minLength: 12,
  maxLength: 128,
  requireLowercase: true,
  requireUppercase: true,
  requireNumber: true,
  requireSpecialCharacter: true,
  specialCharacters: '!@#$%^&*(),.?\":{}|<>',
  contextDenylist: ['snoball', 'snowball'],
  contextMinimumCharacters: 4,
  sequenceLength: 4,
  commonPasswords: DEFAULT_COMMON_PASSWORDS
};

/**
 * One fetch of the public password policy, shared by every field that shows the rules.
 *
 * A plain root service rather than an NgRx store, like `organization-sso.service`: this is
 * read-mostly configuration consumed by unauthenticated pages, and it has no actions worth
 * dispatching. Three properties matter and each is deliberate:
 *
 * - `shareReplay(1)` so the reset screen, the add-user form and the login page make one
 *   request between them instead of one each, and a late subscriber still gets the answer.
 * - `catchError` returning the defaults unconditionally, so a caller never has to handle a
 *   failure. A password field must never hard-fail, or disable submit, on a policy fetch.
 * - the returned object is always complete, because the endpoint sends only the fields it
 *   publishes and a partial merge would leave a rule undefined and its check meaningless.
 */
@Injectable({providedIn: 'root'})
export class PasswordPolicyService {
  private authenticationService = inject(AuthenticationService);

  private cached: Observable<PasswordPolicy> | null = null;

  getPolicy(): Observable<PasswordPolicy> {
    if (!this.cached) {
      this.cached = this.authenticationService.getPublicPolicy().pipe(
        map((response: any) => ({
          ...DEFAULT_PASSWORD_POLICY,
          ...(response?.data?.password || {})
        })),
        catchError(() => of({...DEFAULT_PASSWORD_POLICY})),
        shareReplay({bufferSize: 1, refCount: false})
      );
    }
    return this.cached;
  }

  /** Drop the cached answer, for a test or after the policy is known to have changed. */
  reset(): void {
    this.cached = null;
  }
}
